import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { SubscriptionFlagsNames } from '~/api/Customer';
import { company } from '~/company/Company';
import Icon from '~/components/Icon/Icon';
import { usePushNotifications } from '~/hooks/usePushNotifications';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';

import { mainStore, orderStore, userStore } from '~/stores';

import ContentBottomSheet from './components/ContentBottomSheet/ContentBottomSheet';
import ProgressIndicator from './components/ProgressIndicator/ProgressIndicator';
import { useStepData } from './hooks/useStepData';
import styles from './Onboarding.module.scss';

const Onboarding = () => {
  const navigate = useNavigate();
  const { updateDeviceAndSendTags } = usePushNotifications();
  const {
    handleChangeScreenIndex,
    handleTimerAlive,
    currentStepData,
    currentStep,
    additionalData,
    stepsNumber,
  } = useStepData();

  useEffect(() => {
    const dialogPayload = {
      type: 'no_store',
      postcode: userStore.deliveryAddress?.zip || '',
      lat: userStore.deliveryAddress?.coordinates.lat || '',
      lon: userStore.deliveryAddress?.coordinates.lng || '',
    };
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_SHOWN,
      dialogPayload,
    );
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_SHOWN_BI,
      dialogPayload,
    );
  }, []);

  const sendOnboardingSuccesfull = () => {
    mainStore.sendToRN('sendTags', {
      onboarding_succesfull: true,
    });
    mainStore.sendToRN('sendTags', {
      onboarding_succesfull_date: Math.floor(Date.now() / 1000),
    });
  };

  const handleLaterPress = () => {
    if (currentStep === stepsNumber - 1) {
      const skipPayload = {
        answer: 'skip',
        type: 'no_store',
        postcode: userStore.deliveryAddress?.zip || '',
        lat: userStore.deliveryAddress?.coordinates.lat || '',
        lon: userStore.deliveryAddress?.coordinates.lng || '',
      };
      analyticsService.track(
        AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED,
        skipPayload,
      );
      analyticsService.track(
        AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED_BI,
        skipPayload,
      );
      handleTimerAlive(false);
      mainStore.setOnboardingSeen(true);
      subscibeToNotifications();
      sendOnboardingSuccesfull();
      if (!company.newDesignSystemEnabled) {
        mainStore.sendToRN('setStatusBar', 'dark-content');
      }
      navigate('/', {
        replace: true,
        state: { isShowDeliveryModal: true },
      });
    } else {
      handleChangeScreenIndex(stepsNumber - 1);
    }
  };

  const handleNextPress = () => {
    handleTimerAlive(false);
    handleChangeScreenIndex(currentStep + 1);
  };

  const handlePrevPress = () => {
    handleTimerAlive(false);
    handleChangeScreenIndex(currentStep - 1);
  };

  const handleSubmitForm = ({ name = '', email = '' }) => {
    const nextPayload = {
      answer: 'next',
      type: 'no_store',
      postcode: userStore.deliveryAddress?.zip || '',
      lat: userStore.deliveryAddress?.coordinates.lat || '',
      lon: userStore.deliveryAddress?.coordinates.lng || '',
    };
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED,
      nextPayload,
    );
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED_BI,
      nextPayload,
    );
    const onboardingCompletePayload = {
      eta: orderStore.etaCalculation?.duration.min || 0,
      type: 'passed',
    };
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_COMPLETE,
      onboardingCompletePayload,
    );
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_COMPLETE_BI,
      onboardingCompletePayload,
    );
    mainStore.setOnboardingSeen(true);

    const { middleName, firstName, lastName } = userStore.splitFullName(name);

    if (email) {
      mainStore.sendToRN('setOneSignalUserEmail', email);
    }

    userStore.setDraftUserData({
      middleName,
      firstName,
      lastName,
      email,
    });
    subscibeToNotifications(
      `${firstName || ''} ${middleName || ''} ${lastName || ''}`,
      email,
    );

    if (!company.newDesignSystemEnabled) {
      mainStore.sendToRN('setStatusBar', 'dark-content');
    }
    navigate('/', { replace: true, state: { isShowDeliveryModal: true } });
  };

  const subscibeToNotifications = (firstName = '', email = '') => {
    updateDeviceAndSendTags(firstName, email, [
      {
        name: SubscriptionFlagsNames.NEWS,
        enabled: true,
      },
      {
        name: SubscriptionFlagsNames.REMINDERS,
        enabled: true,
      },
      {
        name: SubscriptionFlagsNames.RECOMMENDATIONS,
        enabled: true,
      },
      {
        name: SubscriptionFlagsNames.SPECIAL_DEALS,
        enabled: true,
      },
      {
        name: SubscriptionFlagsNames.NEWS_EMAIL,
        enabled: true,
      },
      {
        name: SubscriptionFlagsNames.REMINDERS_EMAIL,
        enabled: true,
      },
      {
        name: SubscriptionFlagsNames.RECOMMENDATIONS_EMAIL,
        enabled: true,
      },
      {
        name: SubscriptionFlagsNames.SPECIAL_DEALS_EMAIL,
        enabled: true,
      },
    ]);
  };

  return (
    <div
      className={classes([
        styles.onboarding,
        company.hasNewOnboardingDesign && styles.new,
      ])}
      style={{
        background: additionalData?.backgroundColor,
      }}
      data-company={company.name}
    >
      {company.hasOnboardingDotsInHeader && (
        <ProgressIndicator
          stepsNumber={stepsNumber}
          activeStep={currentStep}
          noPadding={false}
        />
      )}
      {company.hasOnboardingCloseButton && (
        <button onClick={handleLaterPress} className={styles.closeButton}>
          <Icon className={styles.closeIcon} type="close" />
        </button>
      )}
      <div className={styles.imageContainer}>
        <div
          className={classes([
            styles.image,
            company.hasNewOnboardingDesign && styles.new,
            currentStep === stepsNumber - 1 && styles.isLarge,
          ])}
          style={{
            backgroundImage: `url('${currentStepData?.img}')`,
            backgroundPosition: currentStepData?.position || 'bottom',
          }}
        />
      </div>
      <ContentBottomSheet
        isVisibleInputs={currentStep === stepsNumber - 1}
        isLatestStep={currentStep === stepsNumber - 1}
        isVisibleInputLabels={additionalData?.isVisibleInputLabels}
        skipButtonText={additionalData?.skipButtonText}
        emailPlaceholder={additionalData?.inputEmailPlaceholder}
        firstNamePlaceholder={additionalData?.inputFirstNamePlaceholder}
        currentPage={currentStep}
        onNextPress={handleNextPress}
        onSubmitForm={handleSubmitForm}
        onLaterPress={handleLaterPress}
        onPrevPress={handlePrevPress}
        buttonsHeader={
          company.hasOnboardingDotsAboveButtons && (
            <ProgressIndicator
              stepsNumber={stepsNumber}
              activeStep={currentStep}
              noPadding={true}
            />
          )
        }
        title={currentStepData?.textTitle || ''}
        description={currentStepData?.textDescription || ''}
      />
    </div>
  );
};

export default observer(Onboarding);
